// services/firestoreService.ts
import {
  addDoc,
  collection,
  deleteDoc,
  doc,
  getDoc,
  limit,
    getDocs,
  getDocsFromServer,
  query,
  runTransaction,
  serverTimestamp,
  setDoc,
  updateDoc,
  where,

} from 'firebase/firestore';
import {
  getStorage,
  ref as storageRef,
  uploadBytes,
  getDownloadURL,
} from 'firebase/storage';
import { auth, db } from '../firebase/config';
import { Role, Tenant, UserProfile, Partner } from '../types';
import type { User as FirebaseAuthUser } from 'firebase/auth';

const API_BASE_URL = String(import.meta.env.VITE_API_BASE_URL || '').replace(/\/+$/, '');

if (!API_BASE_URL) {
  throw new Error('Missing VITE_API_BASE_URL. It must point to runmodule-api.');
}

async function authedPost(path: string, body: any) {
  const user = auth.currentUser;
  if (!user) throw new Error('not_signed_in');

  // force refresh for deterministic perms after role changes
  const token = await user.getIdToken(true);

  const r = await fetch(`${API_BASE_URL}${path}`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${token}`,
    },
    body: JSON.stringify(body || {}),
  });

  const j = await r.json().catch(() => ({}));
  if (!r.ok) {
    throw new Error(String(j?.error || j?.message || `http_${r.status}`));
  }
  return j;
}


function normalizeEmail(raw: any): string {
  const email = String(raw || '').trim().toLowerCase();
  return email;
}

function normalizeRole(role: any): string {
  return String(role || '').trim().toLowerCase();
}

function makeInviteId(input: { email: string; tenantId?: string | null; role: string }): string {
  const email = normalizeEmail(input.email);
const tenantId = String(input.tenantId || '').trim();
  const role = normalizeRole(input.role);
  // Deterministic, tenant-scoped when tenantId exists. For non-tenant roles, tenantId stays blank.
  return `${email}__${tenantId || 'global'}__${role}`;
}

export const firestoreService = {

  // -----------------------------
  // Auth bootstrap helpers
  // -----------------------------
  async getUserProfile(uid: string): Promise<UserProfile | null> {
    const ref = doc(db, 'users', uid);
    const snap = await getDoc(ref);
    if (!snap.exists()) return null;
const data = snap.data() as any;
return { uid, ...data } as UserProfile;
  },

  async getTenantData(tenantId: string): Promise<Tenant | null> {
    if (!tenantId) return null;
    const ref = doc(db, 'tenants', tenantId);
    const snap = await getDoc(ref);
    if (!snap.exists()) return null;
    return snap.data() as Tenant;
  },
async getTenant(tenantId: string): Promise<Tenant | null> {
  return this.getTenantData(tenantId);
},
async uploadTenantLogoFile(tenantId: string, file: File): Promise<string> {
  const t = String(tenantId || '').trim();
  if (!t) throw new Error('missing_tenantId');
  if (!file) throw new Error('missing_file');

  const user = auth.currentUser;
  if (!user) throw new Error('not_signed_in');

  const token = await user.getIdToken(true);

  const base64 = await new Promise<string>((resolve, reject) => {
    const r = new FileReader();
    r.onerror = () => reject(new Error('file_read_failed'));
    r.onload = () => {
      const s = String(r.result || '');
      const comma = s.indexOf(',');
      resolve(comma >= 0 ? s.slice(comma + 1) : s);
    };
    r.readAsDataURL(file);
  });

  const res = await fetch(`${API_BASE_URL}/uploadTenantLogo`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${token}`,
    },
    body: JSON.stringify({
      tenantId: t,
      fileName: file.name || 'logo',
      contentType: file.type || 'application/octet-stream',
      base64,
    }),
  });

  const out = await res.json().catch(() => ({} as any));
  if (!res.ok) throw new Error(String((out as any)?.message || (out as any)?.error || 'upload_failed'));

  const url = String((out as any)?.url || '').trim();
  if (!url) throw new Error('upload_failed_missing_url');

  return url;
},
async updateTenantLogoUrl(tenantId: string, url: string): Promise<void> {
  const t = String(tenantId || '').trim();
  if (!t) throw new Error('missing_tenantId');

  const u = String(url || '').trim();
  if (!u) throw new Error('missing_logo_url');

  const ref = doc(db, 'tenants', t);
  await updateDoc(ref, { tenantLogoUrl: u });
},
  // -----------------------------
  // Tenant-scoped admin reads
  // -----------------------------
  async getUsersForTenant(tenantId: string): Promise<UserProfile[]> {
  if (!tenantId) return [];

  // Must match what's stored in users.role. Your rules use roleLower() on userData().role,
  // and your UI displays roles like TENANT_ADMIN, TEACHER_USER. So keep uppercase here.
  const allowedRoles = [Role.TEACHER_USER, Role.TENANT_ADMIN] as any[];

  const qRef = query(
    collection(db, 'users'),
    where('tenantId', '==', tenantId),
    where('role', 'in', allowedRoles)
  );

const snap = await getDocsFromServer(qRef);
  return snap.docs
    .map((d) => ({ uid: d.id, ...(d.data() as any) } as UserProfile))
    .filter((u) => !!String(u.email || '').trim());
},

  // Compatibility alias (UI calls this name in your component)
  async getInvitationsForTenant(tenantId: string): Promise<any[]> {
    return this.listInvitationsForTenant(tenantId);
  },
  // -----------------------------
  // Tenant admin actions
  // -----------------------------
  async updateUserStatus(uid: string, status: string): Promise<void> {
    const userId = String(uid || '').trim();
    if (!userId) throw new Error('missing_uid');

    const s = String(status || '').trim().toUpperCase();
    if (!s) throw new Error('missing_status');

    // Change ONLY "status" to satisfy firestore.rules changedKeys().hasOnly(["status"])
    const ref = doc(db, 'users', userId);
    await updateDoc(ref, { status: s });
  },
  // -----------------------------
  // Partner Admin: tenant-scoped user management reads
  // -----------------------------
  async getUsersForPartnerTenant(partnerId: string, tenantId: string): Promise<UserProfile[]> {
    const p = String(partnerId || '').trim();
    const t = String(tenantId || '').trim();
    if (!p || !t) return [];

    const allowedRoles = [Role.TEACHER_USER, Role.TENANT_ADMIN] as any[];

    // Query by tenantId + role; rely on rules to enforce partner linkage.
    const qRef = query(
      collection(db, 'users'),
      where('tenantId', '==', t),
      where('role', 'in', allowedRoles)
    );

    const snap = await getDocs(qRef);

    return snap.docs
      .map((d) => ({ uid: d.id, ...(d.data() as any) } as UserProfile))
      .filter((u) => !!String(u.email || '').trim());
  },

  async getInvitationsForPartnerTenant(partnerId: string, tenantId: string): Promise<any[]> {
    const p = String(partnerId || '').trim();
    const t = String(tenantId || '').trim();
    if (!p || !t) return [];

    const allowedRoles = ['TEACHER_USER', 'TENANT_ADMIN'];

    const qRef = query(
      collection(db, 'invitations'),
      where('tenantId', '==', t),
      where('role', 'in', allowedRoles)
    );

  const snap = await getDocsFromServer(qRef);
    return snap.docs.map((d) => ({ id: d.id, ...(d.data() as any) }));
  },
  // -----------------------------
  // Credits
  // -----------------------------
  async addTenantCredits(tenantId: string, amount: number, type: 'free' | 'paid'): Promise<Tenant> {
    if (!tenantId) throw new Error('Missing tenantId.');
    if (!Number.isFinite(amount) || amount <= 0) throw new Error('Invalid credits amount.');

    console.log(`[Firestore Service] Adding ${amount} ${type} credits to tenant ${tenantId}.`);

    const tenantRef = doc(db, 'tenants', tenantId);

    try {
      const updatedTenantData = await runTransaction(db, async (transaction) => {
        const tenantSnap = await transaction.get(tenantRef);
        if (!tenantSnap.exists()) throw new Error('Tenant not found.');

        const currentTenant = tenantSnap.data() as Tenant;

        const newBalance = { ...(currentTenant.creditBalance || {}) };
        if (type === 'free') newBalance.freeRemaining = Number(newBalance.freeRemaining || 0) + amount;
        if (type === 'paid') newBalance.paidRemaining = Number(newBalance.paidRemaining || 0) + amount;

        transaction.update(tenantRef, { creditBalance: newBalance });

        const ledgerRef = doc(collection(db, `tenants/${tenantId}/credit_ledger`));
        transaction.set(ledgerRef, {
          type: 'CREDIT_TOPUP',
          pool: type,
          credits: amount,
          createdAt: serverTimestamp(),
        });

        return { ...currentTenant, id: tenantId, creditBalance: newBalance } as Tenant;
      });

      console.log(
        `[Firestore Service] Transaction successful. New balance for ${tenantId}:`,
        updatedTenantData.creditBalance,
      );
      return updatedTenantData;
    } catch (error) {
      console.error('[Firestore Service] Transaction failed:', error);
      throw new Error('Failed to update credits. The operation was rolled back.');
    }
  },

async createUser(
  input: {
    email: string;
    role: Role | string;
    tenantId?: string | null;
    partnerId?: string | null;
    displayName?: string | null;
  },
  actorProfile: UserProfile
): Promise<{ inviteId: string }> {
  const createdByUid = String(actorProfile?.uid || auth.currentUser?.uid || '').trim();
if (!createdByUid) throw new Error('missing_createdByUid');

  const email = normalizeEmail(input.email);
  const role = input.role as any;

  const tenantId = input.tenantId ? String(input.tenantId).trim() : null;
let partnerId: string | null = null;

if (input.partnerId) {
  partnerId = String(input.partnerId).trim();
} else if (tenantId) {
  const t = await this.getTenantData(tenantId);
  partnerId = String((t as any)?.partnerId || '').trim() || null;
}
  let tenantName: string | null = null;
  if (tenantId) {
    const t = await this.getTenantData(tenantId);
    tenantName = String((t as any)?.tenantName || (t as any)?.name || '').trim() || null;
  }

  return await this.createInvitation({
    email,
    role,
    tenantId,
    tenantName,
    partnerId,
    displayName: input.displayName ? String(input.displayName) : null,
    createdByUid,
  });
},
  // -----------------------------
  // Invites (Flow 2 deterministic claim)
  // -----------------------------
  async createInvitation(params: {
  email: string;
  role: Role | string;
  tenantId?: string | null;
  tenantName?: string | null;
  partnerId?: string | null;
  displayName?: string | null;
  createdByUid: string;
}): Promise<{ inviteId: string }> {

  const email = normalizeEmail(params.email);
  const role = params.role as any;
  const roleNorm = normalizeRole(role);

  const tenantId = params.tenantId ? String(params.tenantId).trim() : null;
  const tenantName = params.tenantName ? String(params.tenantName).trim() : null;
  const partnerId = params.partnerId ? String(params.partnerId).trim() : null;

  // -----------------------------
  // Tenant role enforcement
  // -----------------------------
  const tenantScoped =
    role === Role.TENANT_ADMIN ||
    role === Role.TEACHER_USER ||
    roleNorm === 'tenant_admin' ||
    roleNorm === 'teacher_user';

  if (tenantScoped && !tenantId) {
    throw new Error('tenant_required_for_role');
  }

  // -----------------------------
  // Partner role enforcement  ← ADD THIS BLOCK HERE
  // -----------------------------
  if (roleNorm === 'partner_admin') {
    if (tenantId) {
      throw new Error('partner_role_cannot_have_tenant');
    }
    if (!partnerId) {
      throw new Error('partner_required_for_role');
    }
  }

  // -----------------------------
  // Invite ID generation
  // -----------------------------
  const inviteId = makeInviteId({ email, tenantId, role: roleNorm });

  const invitationRef = doc(db, 'invitations', inviteId);

await setDoc(invitationRef, {
  inviteId,
  email,           // normalized lower
  emailLower: email, // deterministic dual-write for backend lookup

  role,
  roleNorm,

  tenantId: tenantId || null,
  tenantName: tenantName || null,
  partnerId: partnerId || null,

  displayName: params.displayName || null,
  status: 'PENDING',
  createdAt: serverTimestamp(),
  createdByUid: params.createdByUid,
});

  return { inviteId };
},

 async listInvitationsForTenant(tenantId: string): Promise<any[]> {
  if (!tenantId) return [];
  try {
    const allowedRoles = ['TEACHER_USER', 'TENANT_ADMIN'];

    const qRef = query(
      collection(db, 'invitations'),
      where('tenantId', '==', tenantId),
      where('role', 'in', allowedRoles)
    );

const snap = await getDocsFromServer(qRef);
    return snap.docs.map((d) => ({ id: d.id, ...(d.data() as any) }));
  } catch (e) {
    console.error('[Firestore Service] invitations query failed', e);
    return [];
  }
},

  async revokeInvitationByEmail(inviteId: string): Promise<void> {
    if (!inviteId) throw new Error('Missing inviteId.');
    const invitationRef = doc(db, 'invitations', String(inviteId).trim());
    await deleteDoc(invitationRef);
  },

  async listMyPendingInvites(user: FirebaseAuthUser): Promise<any[]> {
    const token = await user.getIdToken(true);

    const res = await fetch(`${API_BASE_URL}/myPendingInvites`, {
      method: 'GET',
      headers: { Authorization: `Bearer ${token}` },
    });

    if (!res.ok) {
      const body = await res.json().catch(() => ({}));
      console.error('[Firestore Service] myPendingInvites failed', res.status, body);
      throw new Error((body as any)?.error || 'my_pending_invites_failed');
    }

    const data = await res.json().catch(() => ({}));
    return Array.isArray((data as any)?.invites) ? (data as any).invites : [];
  },

  async claimInviteById(user: FirebaseAuthUser, inviteId: string): Promise<UserProfile> {
    const cleaned = String(inviteId || '').trim();
    if (!cleaned) throw new Error('missing_invite_id');

    const token = await user.getIdToken(true);
// TEMP DEBUG (remove after)
console.log('[claimInviteById] uid/email/inviteId', {
  uid: user?.uid,
  email: String((user as any)?.email || ''),
  inviteId: cleaned,
});
    const res = await fetch(`${API_BASE_URL}/claimInvite`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`,
      },
      body: JSON.stringify({ inviteId: cleaned }),
    });

    const result = await res.json().catch(() => ({} as any));
if (!res.ok) {
  console.error('[Firestore Service] claimInvite failed', res.status, result);
  throw new Error((result as any)?.error || 'claim_invite_failed');
}

const profile = (result as any)?.profile as UserProfile | undefined;
if (!profile || !String((profile as any).uid || '').trim()) {
  throw new Error('claim_invite_missing_profile');
}

return profile;
  },

  async getInvitationAndClaim(user: FirebaseAuthUser): Promise<UserProfile | null> {
    // Legacy support: if App stored a specific inviteId, claim that.
    const storedInviteId = (() => {
      try {
        return window.localStorage.getItem('osfn_pending_invite_id');
      } catch (_) {
        return null;
      }
    })();

    const inviteId =
      storedInviteId && String(storedInviteId).trim() ? String(storedInviteId).trim() : null;

    if (inviteId) {
      try {
        window.localStorage.removeItem('osfn_pending_invite_id');
      } catch (_) {}
      return await this.claimInviteById(user, inviteId);
    }

    // Flow 2: if exactly one pending invite exists for token email, auto-claim.
    const invites = await this.listMyPendingInvites(user);
    if (invites.length === 1 && invites[0]?.inviteId) {
      return await this.claimInviteById(user, String(invites[0].inviteId));
    }

    // None or many: let caller handle (UI picker for many).
    return null;
  },

  // -----------------------------
  // Bulk user creation (deterministic invites)
  // -----------------------------
async bulkAddUsers(params: {
  createdByUid: string;
  tenantId?: string | null;
  tenantName?: string | null;
  partnerId?: string | null;
  rows: Array<{
    email: string;
    role: Role | string;
    displayName?: string | null;
  }>;
}): Promise<{ created: number; skipped: number; errors: any[] }> {
  const createdByUid = String(params.createdByUid || '').trim();
  if (!createdByUid) throw new Error('missing_createdByUid');

  const tenantId = params.tenantId ? String(params.tenantId).trim() : null;
  const tenantName = params.tenantName ? String(params.tenantName).trim() : null;
  const partnerId = params.partnerId ? String(params.partnerId).trim() : null;

  const rows = Array.isArray(params.rows) ? params.rows : [];
  const errors: any[] = [];

  // This bulk flow is tenant-scoped. Do not allow "global" IDs.
  if (!tenantId) throw new Error('missing_tenantId');

  // Deduplicate in-memory by computed inviteId, deterministic.
  const unique = new Map<string, any>();

  for (const r of rows) {
    const email = normalizeEmail((r as any)?.email);
    const roleRaw = String((r as any)?.role || '').trim();
    const roleNorm = normalizeRole(roleRaw);

    // Reject blank/invalid rows (prevents "__tenant__" or "__global__" docs)
    if (!email || !email.includes('@')) {
      errors.push({ email, role: roleRaw, error: 'row_missing_or_invalid_email' });
      continue;
    }

    // Only allow tenant scoped roles here
    if (roleNorm !== 'tenant_admin' && roleNorm !== 'teacher_user') {
      errors.push({ email, role: roleRaw, error: 'role_not_allowed_for_bulk' });
      continue;
    }

    const inviteId = makeInviteId({ email, tenantId, role: roleNorm });

    if (!unique.has(inviteId)) {
      unique.set(inviteId, {
        inviteId,
        email,
        role: roleRaw.toUpperCase(),
        roleNorm,
        tenantId,
        tenantName: tenantName || null,
        partnerId: partnerId || null,
        displayName: (r as any)?.displayName ? String((r as any).displayName).trim() : null,
      });
    }
  }

  // Deterministic ordering of operations
  const items = Array.from(unique.values()).sort((a, b) =>
    String(a.inviteId).localeCompare(String(b.inviteId))
  );

  // If nothing valid, return the row errors as "rejected"
  if (!items.length) {
    return { created: 0, skipped: 0, errors };
  }

  // Check existing invites by doc id
 const existingInviteIds = new Set<string>();
for (const it of items) {
  try {
    const ref = doc(db, 'invitations', it.inviteId);
    const snap = await getDoc(ref);
    if (snap.exists()) existingInviteIds.add(it.inviteId);
  } catch (e: any) {
    const code = String(e?.code || '').toLowerCase();

    // Partner Admin cannot "read missing doc to check existence" due to rules.
    // Treat permission-denied as "unknown/not-existing" and proceed to create.
    if (code === 'permission-denied') {
      continue;
    }

    errors.push({
      inviteId: it.inviteId,
      email: it.email,
      error: 'invite_read_failed',
      code: e?.code || null,
    });
  }
}

  let created = 0;
  let skipped = 0;

  // Batch writes in chunks (<= 450)
  let batch = writeBatch(db);
  let ops = 0;

  const flush = async () => {
    if (ops === 0) return;
    await batch.commit();
    batch = writeBatch(db);
    ops = 0;
  };

  for (const it of items) {
    const already = existingInviteIds.has(it.inviteId);
    if (already) {
      skipped += 1;
      continue;
    }

    const ref = doc(db, 'invitations', it.inviteId);

    batch.set(ref, {
  inviteId: it.inviteId,
  email: it.email,            // normalized lower already
  emailLower: it.email,       // deterministic dual-write for backend lookup

  role: it.role,
  roleNorm: it.roleNorm,

  tenantId: it.tenantId,
  tenantName: it.tenantName,
  partnerId: it.partnerId,
  displayName: it.displayName,

  status: 'PENDING',
  createdAt: serverTimestamp(),
  updatedAt: serverTimestamp(),
  createdByUid,
  source: 'BULK_UPLOAD',
});
    created += 1;
    ops += 1;

    if (ops >= 450) await flush();
  }

  await flush();

  return { created, skipped, errors };
},
  // -----------------------------
  // Admin data reads used by console
  // -----------------------------
  async getAllTenants(): Promise<any[]> {
  const snap = await getDocs(collection(db, 'tenants'));
  return snap.docs.map((d) => {
    const data = d.data() as any;
    return {
      id: d.id,
      ...data,
      // UI expects "name"
      name: String(data?.name || data?.tenantName || '').trim(),
      partnerId: String(data?.partnerId || '').trim(),
    };
  });
},
 async getAllPartners(): Promise<any[]> {
  const snap = await getDocs(collection(db, 'partners'));
  return snap.docs.map((d) => {
    const data = d.data() as any;
    return {
      id: d.id,
      ...data,
      partnerId: String(data?.partnerId || d.id || '').trim(),
      partnerName: String(data?.partnerName || data?.name || '').trim(),
    };
  });
},

  async getTenantsForPartner(partnerId: string): Promise<any[]> {
    if (!partnerId) return [];
    const q = query(collection(db, 'tenants'), where('partnerId', '==', partnerId));
    const snap = await getDocs(q);
    return snap.docs.map((d) => ({ id: d.id, ...(d.data() as any) }));
  },
async getPartnerByPartnerId(partnerId: string): Promise<any | null> {
  const pid = String(partnerId || '').trim();
  if (!pid) return null;

  try {
    const qSnap = await getDocsFromServer(
  query(collection(db, 'partners'), where('partnerId', '==', pid), limit(1))
);
    if (qSnap.empty) return null;

    const d = qSnap.docs[0];
if (!d) return null;
return { id: d.id, ...(d.data() as any) };
  } catch (e: any) {
    const code = String(e?.code || '').toLowerCase();
    if (code === 'permission-denied') return null;
    throw e;
  }
},
async createPartner(
  partnerName: string,
  contactEmail?: string,
  notes?: string
): Promise<Partner> {

  const generateId = (name: string): string =>
    name
      .toLowerCase()
      .trim()
      .replace(/&/g, 'and')
      .replace(/[^a-z0-9\s-]/g, '')
      .replace(/\s+/g, '-')
      .replace(/-+/g, '-');

  const partnerId = generateId(partnerName);

  const ref = doc(db, 'partners', partnerId);

  const snap = await getDoc(ref);
  if (snap.exists()) {
    throw new Error('partner_already_exists');
  }

  const data: Omit<Partner, 'id'> = {
    partnerId,
    partnerName,
    contactEmail: contactEmail || null,
    notes: notes || null,
    status: 'ACTIVE',
    createdAt: serverTimestamp(),
    billingConfig: {
      creditsPerPage: 1,
      creditsPerRupee: 0.5,
      freePoolCredits: 0
    }
  };

  await setDoc(ref, data);

  return { id: partnerId, ...data } as Partner;
},
async createTenant(
  tenantName: string,
  partnerId: string
): Promise<Tenant> {

  const generateId = (name: string): string =>
    name
      .toLowerCase()
      .trim()
      .replace(/[^a-z0-9\s-]/g, '')
      .replace(/\s+/g, '-')
      .replace(/-+/g, '-');

  const tenantId = generateId(tenantName);

  const ref = doc(db, 'tenants', tenantId);

  const snap = await getDoc(ref);
  if (snap.exists()) {
    throw new Error('tenant_already_exists');
  }

  const data: Omit<Tenant, 'id'> = {
    tenantName,
    partnerId,
    status: 'ACTIVE',
    createdAt: serverTimestamp(),
    customPrompts: {},
    entitlements: {
      aTypicalModule: true,
      shapeShifterModule: true,
      practiceMakesPermanentModule: true,
      theAssessorModule: true,
    },
    limits: {
      maxPagesPerRun: 20,
      maxFileSizeMb: 10,
      maxRunsPerDay: 50,
    },
    billingConfig: {
      creditsPerRupee: 0.5,
      creditsPerPage: 1,
      freePoolCredits: 0
    },
    creditBalance: {
      freeRemaining: 0,
      paidRemaining: 0,
      freeCreditExpiryTimestamp: null,
    }
  };

  await setDoc(ref, data);

  return { id: tenantId, ...data } as Tenant;
},

  // -----------------------------
  // Misc
  // -----------------------------
  async signOut(): Promise<void> {
    await auth.signOut();
  },

async uploadLearningOutcomes(
  board: any,
  meta: { doc_id: string; version: string; title?: string; scope?: string },
  items: any[]
): Promise<{ boardKey: string; docId: string; version: string }> {
  const boardKey = String(board || '').trim().toUpperCase();
  if (!boardKey) throw new Error('missing_board');

  const docId = String((meta as any)?.doc_id || '').trim();
  if (!docId) throw new Error('missing_doc_id');

  const version = String((meta as any)?.version || '').trim() || new Date().toISOString();
  const title = String((meta as any)?.title || '').trim() || null;
  const scope = String((meta as any)?.scope || '').trim() || null;

  const safeItems = (Array.isArray(items) ? items : [])
    .filter(Boolean)
    .map((it: any, idx: number) => {
      const subject = String(it?.subject || '').trim();
      const outcome = String(it?.outcome || it?.lo_text || it?.text || '').trim();
      const grade = String(it?.grade || '').trim() || null;
      const topic = String(it?.topic || '').trim() || null;
      const id = String(it?.id || `${docId}_${idx}`).trim();
      return { id, board: String(it?.board || boardKey).trim().toUpperCase() || boardKey, subject, grade, topic, outcome, lo_text: outcome, doc_id: docId, version, title, scope };
    })
    .filter((it: any) => Boolean(it.subject) && Boolean(String(it.outcome || '').trim()));

  await authedPost('/admin/uploadLearningOutcomes', { board: boardKey, meta: { doc_id: docId, version, title, scope }, items: safeItems });

  return { boardKey, docId, version };
},



async updateTenantPartner(tenantId: string, partnerId: string) {
  const j = await authedPost('/admin/updateTenantPartner', {
    tenantId: String(tenantId || '').trim(),
    partnerId: String(partnerId || '').trim(),
  });
  return j?.tenant || null;
},

async updateTenantPrompts(tenantId: string, prompts: any) {
  const j = await authedPost('/admin/updateTenantPrompts', {
    tenantId: String(tenantId || '').trim(),
    customPrompts: prompts || {},
  });
  return j?.tenant || null;
},

async addCreditsToTenant(
  tenantId: string,
  amount: number,
  creditType: 'paid' | 'free',
  memo?: string
) {
  const j = await authedPost('/admin/addTenantCredits', {
    tenantId: String(tenantId || '').trim(),
    amount: Number(amount || 0),
    creditType,
    memo: String(memo || ''),
  });
  return j?.tenant || null;
},

async updatePartnerConfig(
  partnerDocId: string,
  patch: { contactEmail?: string; status?: string }
) {
  const j = await authedPost('/admin/updatePartnerConfig', {
    partnerDocId: String(partnerDocId || '').trim(),
    contactEmail: String(patch?.contactEmail || ''),
    status: String(patch?.status || 'ACTIVE'),
  });
  return j?.partner || null;
},

async addPartnerCredits(
  partnerDocId: string,
  amount: number,
  creditType: 'paid' | 'free',
  memo?: string
) {
  const j = await authedPost('/admin/addPartnerCredits', {
    partnerDocId: String(partnerDocId || '').trim(),
    amount: Number(amount || 0),
    creditType,
    memo: String(memo || ''),
  });
  return j?.partner || null;
},
};
export default firestoreService;